// BlogSection.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./BlogSection.css";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.goharesadaf.ir";

/**
 * BlogSection
 * Props:
 *  - limit (number) optional: number of posts to show
 */
export default function BlogSection({ limit = 4 }) {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function fetchBlogs() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/api/blogs`);
        if (!res.ok) {
          const text = await res.text().catch(() => null);
          throw new Error(text || "Failed to fetch blogs");
        }
        const data = await res.json();
        if (!mounted) return;
        // if API returns paginated object, adapt accordingly
        const list = Array.isArray(data) ? data : (data.data ? data.data : []);
        setBlogs(list.slice(0, limit));
      } catch (err) {
        console.error("BlogSection fetch error:", err);
        if (mounted) setError(err.message || "خطا در بارگذاری بلاگ‌ها");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchBlogs();
    return () => { mounted = false; };
  }, [limit]);

  const stripHtml = (html) => {
    if (!html) return "";
    // Use DOMParser to preserve correct decoding
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      return doc.body.textContent || "";
    } catch (e) {
      // fallback
      return html.replace(/<[^>]*>/g, "");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("fa-IR");
    } catch {
      return iso;
    }
  };

  if (loading) {
    // simple skeleton grid
    return (
      <section className="blog-section">
        <div className="blog-header">
          <h2>آخرین مطالب</h2>
          <Link className="view-all-link" to="/blogs">مشاهده همه</Link>
        </div>

        <div className="blog-grid">
          {Array.from({ length: Math.min(4, limit) }).map((_, i) => (
            <article key={i} className="blog-card skeleton">
              <div className="thumb" />
              <div className="card-body">
                <div className="meta" />
                <h3 className="title" />
                <p className="excerpt" />
                <div className="read-more" />
              </div>
            </article>
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="blog-section">
        <div className="blog-header">
          <h2>آخرین مطالب</h2>
          <Link className="view-all-link" to="/blogs">مشاهده همه</Link>
        </div>
        <div className="blog-error">خطا در بارگذاری بلاگ‌ها: {error}</div>
      </section>
    );
  }

  return (
    <section className="blog-bg-full">
      <div className="blog-section">
        <div className="blog-header">
          <h2>آخرین مطالب</h2>
          <Link className="view-all-link" to="/blogs">مشاهده همه</Link>
        </div>

        <div className="blog-grid">
          {blogs.map((b) => (
            <article key={b.id} className="blog-card" aria-labelledby={`blog-title-${b.id}`}>
              <Link to={`/blogs/${b.slug}`} className="thumb-link" aria-hidden="true">
                <img
                  className="thumb-img"
                  src={b.image || "/images/blog-placeholder.png"}
                  alt={b.title || "blog cover"}
                  onError={(e) => { e.currentTarget.src = "/images/blog-placeholder.png"; }}
                />
              </Link>

              <div className="card-body">
                <div className="meta">
                  <time dateTime={b.created_at}>{formatDate(b.created_at)}</time>
                </div>

                <h3 id={`blog-title-${b.id}`} className="title">
                  <Link to={`/blogs/${b.slug}`}>{b.title}</Link>
                </h3>

                <p className="excerpt">
                  {stripHtml(b.content).slice(0, 140)}
                  {stripHtml(b.content).length > 140 ? "…" : ""}
                </p>

                <div className="card-footer">
                  <Link to={`/blogs/${b.slug}`} className="read-more-btn">مطالعه بیشتر</Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );

}
